import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Pencil, RefreshCcw, XCircle, PauseCircle, PlayCircle, Loader2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import type { ApiError } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import {
  subscriptionService,
  type SubscriptionDetail,
  type SubscriptionRenewalHistory,
} from '@/services/subscription.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import { DueDateBadge } from './DueDateBadge';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

function fmt(v: string | null | undefined): string {
  if (!v) return '—';
  return format(new Date(v), 'dd MMM yyyy');
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function RenewalHistorySection({ subscriptionId }: { subscriptionId: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['subscriptions', 'history', subscriptionId],
    queryFn: () => subscriptionService.getHistory(subscriptionId),
    enabled: open,
  });

  return (
    <div className="border-t border-border/60 pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Renewal History
      </button>
      {open && (
        <div className="mt-2">
          {isLoading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : !data?.data.length ? (
            <p className="py-3 text-xs text-muted-foreground">No renewal history yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/60">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Amount</th>
                    <th className="px-3 py-2 text-left font-semibold">New Due</th>
                    <th className="px-3 py-2 text-left font-semibold">By</th>
                    <th className="px-3 py-2 text-left font-semibold">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {data.data.map((h: SubscriptionRenewalHistory) => (
                    <tr key={h.id} className="hover:bg-muted/20">
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(h.renewed_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-medium">{h.amount}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(h.new_due_date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {h.renewed_by_first ? `${h.renewed_by_first} ${h.renewed_by_last ?? ''}`.trim() : <span className="text-muted-foreground">System</span>}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={h.renewal_type === 'auto' ? 'secondary' : 'outline'} className="text-[10px]">
                          {h.renewal_type}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface Props {
  subscriptionId: string | null;
  onClose: () => void;
  onEdit?: (sub: SubscriptionDetail) => void;
  onRenew?: (sub: SubscriptionDetail) => void;
  onCancel?: (sub: SubscriptionDetail) => void;
}

export function SubscriptionDetailDrawer({ subscriptionId, onClose, onEdit, onRenew, onCancel }: Props) {
  const queryClient = useQueryClient();

  const { data: sub, isLoading, isError } = useQuery({
    queryKey: ['subscriptions', 'detail', subscriptionId],
    queryFn: () => subscriptionService.get(subscriptionId!),
    enabled: !!subscriptionId,
  });

  const pauseMut = useMutation({
    mutationFn: (id: string) => subscriptionService.pause(id),
    onSuccess: () => {
      toast.success('Subscription paused');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const resumeMut = useMutation({
    mutationFn: (id: string) => subscriptionService.resume(id),
    onSuccess: () => {
      toast.success('Subscription resumed');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const isPaused = sub?.status_name?.toLowerCase() === 'paused';
  const isCancelled = sub?.status_name?.toLowerCase() === 'cancelled';
  const canRenew = !isCancelled;

  return (
    <Dialog open={!!subscriptionId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        {isLoading && (
          <>
            <DialogTitle className="sr-only">Loading</DialogTitle>
            <div className="space-y-3 p-2">
              {[...Array(8)].map((_, i) => <div key={i} className="h-4 animate-pulse rounded bg-muted" />)}
            </div>
          </>
        )}

        {isError && !isLoading && (
          <>
            <DialogTitle className="sr-only">Error</DialogTitle>
            <p className="py-10 text-center text-sm text-destructive">Failed to load subscription details.</p>
          </>
        )}

        {sub && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold">{sub.name}</span>
                <SubscriptionStatusBadge name={sub.status_name} color={sub.status_color} />
              </DialogTitle>
            </DialogHeader>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 border-b border-border/60 pb-3">
              <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_EDIT}>
                <Button size="sm" variant="outline" onClick={() => onEdit?.(sub)}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_RENEW}>
                <Button size="sm" variant="outline" onClick={() => onRenew?.(sub)} disabled={!canRenew}>
                  <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Renew
                </Button>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_EDIT}>
                {isPaused ? (
                  <Button size="sm" variant="outline" onClick={() => resumeMut.mutate(sub.id)} disabled={resumeMut.isPending}>
                    {resumeMut.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1" />}
                    Resume
                  </Button>
                ) : !isCancelled ? (
                  <Button size="sm" variant="outline" onClick={() => pauseMut.mutate(sub.id)} disabled={pauseMut.isPending}>
                    {pauseMut.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5 mr-1" />}
                    Pause
                  </Button>
                ) : null}
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_CANCEL}>
                <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" onClick={() => onCancel?.(sub)} disabled={isCancelled}>
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
              </PermissionGate>
            </div>

            {/* Info grid */}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 pt-2">
              <InfoRow label="Vendor">{sub.vendor_name ?? <span className="text-muted-foreground">—</span>}</InfoRow>
              <InfoRow label="Category">{sub.category_name ?? <span className="text-muted-foreground">—</span>}</InfoRow>
              <InfoRow label="Department">{sub.department_name ?? <span className="text-muted-foreground">—</span>}</InfoRow>
              <InfoRow label="Assigned To">
                {sub.assigned_first ? `${sub.assigned_first} ${sub.assigned_last ?? ''}`.trim() : <span className="text-muted-foreground">Unassigned</span>}
              </InfoRow>
              <InfoRow label="Billing Cycle">{sub.billing_cycle_name}</InfoRow>
              <InfoRow label="Amount">
                <span className="font-semibold">{sub.currency_symbol}{Number(sub.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                <span className="ml-1 text-xs text-muted-foreground">{sub.currency_code}</span>
              </InfoRow>
              <InfoRow label="Start Date">{fmt(sub.start_date)}</InfoRow>
              <InfoRow label="End Date">{fmt(sub.end_date)}</InfoRow>
              <InfoRow label="Next Due">
                <DueDateBadge nextDueDate={sub.next_due_date} reminderDays={sub.reminder_days} />
              </InfoRow>
              <InfoRow label="Last Renewed">{fmt(sub.last_renewed_at)}</InfoRow>
              <InfoRow label="Renewal Count">{sub.renewal_count}</InfoRow>
              <InfoRow label="Auto Renewal">
                <Badge variant={sub.auto_renewal ? 'success' : 'secondary'}>{sub.auto_renewal ? 'Enabled' : 'Disabled'}</Badge>
              </InfoRow>
              <InfoRow label="Reminder Days">{sub.reminder_days} days before</InfoRow>
              <InfoRow label="Created">{fmt(sub.created_at)}</InfoRow>
            </dl>

            {sub.notes && (
              <div className="border-t border-border/60 pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                <p className="text-sm whitespace-pre-wrap">{sub.notes}</p>
              </div>
            )}

            <RenewalHistorySection subscriptionId={sub.id} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
